import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './shared/Icon';
import ProvenanceSourcesModal from './ProvenanceSourcesModal';

/**
 * The "Inspired by …" provenance line — credits the source trips a trip was
 * built from. Collapses by count (decided with the user):
 *   1 viewable source:        Inspired by @kurtis · Lisbon in 3 Days
 *   1 viewable + N others:    Inspired by @kurtis & N others   (opens modal)
 *   no viewable source (all   Inspired by N trips              (opens modal —
 *     gated) but total > 0:                                     gated rows shown)
 *   total 0:                  renders nothing
 *
 * "@handle" degrades to the creator's name when they haven't claimed a handle.
 * The handle/name in the 1-source case links to the source trip; "& N others"
 * and the count-only form open the source-list modal (the discovery surface).
 *
 * Props:
 *   summary: { total, topSource: { trip_id, trip_name, creator_handle, creator_name } | null }
 *   tripId:  this trip's id — used to fetch the full source list when the modal opens
 *            (feed cards only carry the summary). Omit if `sources` is provided.
 *   sources: optional pre-loaded full source list (detail page has it already) —
 *            passed straight to the modal so it doesn't refetch.
 *   className: optional extra classes for the wrapper.
 */
function ProvenanceLine({ summary, tripId, sources, className = '' }) {
  const { t } = useTranslation('feedCard');
  const [modalOpen, setModalOpen] = useState(false);

  if (!summary || !summary.total) return null;

  const { total, topSource } = summary;
  const othersCount = total - 1;

  const label = topSource
    ? (topSource.creator_handle ? `@${topSource.creator_handle}` : (topSource.creator_name || t('provenanceLine.creatorFallback')))
    : null;

  const openModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setModalOpen(true);
  };

  // Shared sparkles + "Inspired by" prefix
  const prefix = (
    <>
      <Icon name="sparkles" className="w-3.5 h-3.5 inline mr-1 flex-shrink-0 text-primary" />
      {t('provenanceLine.prefix')}{' '}
    </>
  );

  let body;
  if (!topSource) {
    // All sources gated from this viewer → count-only, opens modal (gated rows).
    body = (
      <button type="button" onClick={openModal} className="underline decoration-dotted hover:text-primary transition-colors">
        {t('provenanceLine.tripsCount', { count: total })}
      </button>
    );
  } else if (othersCount <= 0) {
    // Exactly one viewable source — name links to it, plus the trip title.
    body = (
      <>
        <Link
          to={`/shared/trip/${topSource.trip_id}`}
          onClick={(e) => e.stopPropagation()}
          className="font-medium text-content-secondary hover:text-primary transition-colors"
        >
          {label}
        </Link>
        {topSource.trip_name && <span className="text-content-muted"> · {topSource.trip_name}</span>}
      </>
    );
  } else {
    // Top source + "& N others" (others opens the modal).
    body = (
      <>
        <Link
          to={`/shared/trip/${topSource.trip_id}`}
          onClick={(e) => e.stopPropagation()}
          className="font-medium text-content-secondary hover:text-primary transition-colors"
        >
          {label}
        </Link>
        {' & '}
        <button type="button" onClick={openModal} className="underline decoration-dotted hover:text-primary transition-colors">
          {t('provenanceLine.othersCount', { count: othersCount })}
        </button>
      </>
    );
  }

  return (
    <>
      <p className={`text-xs text-content-muted flex items-center flex-wrap ${className}`}>
        {prefix}
        <span className="truncate">{body}</span>
      </p>
      {modalOpen && (
        <ProvenanceSourcesModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          tripId={tripId}
          sources={sources}
        />
      )}
    </>
  );
}

export default ProvenanceLine;
